import LoadingAnimation from "@assets/svg/LoadingAnimation";
import PlusCircleIcon from "@assets/svg/PlusCircleIcon";
import { Button } from "@components/ui/button";
import { acceptOrDeclineInviteConfirmation } from "@endpoints/interview.service";
import { IGetInterviewByIdResponse, Meeting } from "@endpoints/types/interview.type";
import { getMeetingStatus } from "@pages/CalendarsPage/ScheduleSection/ScheduleSection";
import { RootState } from "@store/reducers";
import { TimeSlot } from "@type/meeting.type";
import { EnumUserTypes } from "@utils/constants";
import dayjs from "@lib/dayjs";
import { useSnackbar } from "notistack";
import React, { SetStateAction, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

type InviteConfirmationContainerProps = {
  slot: any;
  interviewDetails: IGetInterviewByIdResponse;
  setInterviewDetails: React.Dispatch<SetStateAction<IGetInterviewByIdResponse>>;
};

const InviteConfirmationContainer = (
  props: InviteConfirmationContainerProps
) => {
  const navigate = useNavigate();
  const { slot, interviewDetails, setInterviewDetails } = props;
  const { enqueueSnackbar } = useSnackbar();
  const user = useSelector((state: RootState) => state.auth?.user);

  // Local states
  const [isAccepting, setIsAccepting] = useState(false);
  const [isDeclining, setIsDeclining] = useState(false);
  const date = dayjs(slot?.startTime).format(`dddd MMM Do`);
  const isMeetingDeclined =
    interviewDetails?.interviewStatus?.toLowerCase() === "declined";
  const isAccepted =
    user?.userType === EnumUserTypes.CANDIDATE
      ? interviewDetails?.isCandidateConfirm
      : interviewDetails?.isCompanyConfirm;

  const handleAcceptOrDecline = async (type: "accept" | "decline") => {
    try {
      if (type === "accept") {
        setIsAccepting(true);
      } else {
        setIsDeclining(true);
      }
      const { success, data } = await acceptOrDeclineInviteConfirmation({
        id: interviewDetails?._id,
        isConfirmInterview: type === "accept",
      });
      if (success) {
        if (type === "accept") {
          setInterviewDetails(data);
          navigate("/calendar/meeting/schedules");
        } else {
          setInterviewDetails(data);
        }
        enqueueSnackbar(
          `Invitation ${
            type === "accept" ? "accepted" : "declined"
          } successfully.`,
          { variant: "success" }
        );
      }
    } catch (error) {
      console.error("Error: accepting or declining invite confirmation", error);
    } finally {
      setIsAccepting(false);
      setIsDeclining(false);
    }
  };

  const handleCounterProposalPoll = () => {
    navigate(`/counter-proposal-poll?id=${interviewDetails?._id}`);
  };

  return (
    <div>
      <div className="grid grid-cols-[1fr_0.5fr] mt-10">
        {/* Col 1 */}
        <div className="flex flex-col gap-3">
          <span className="font-semibold text-xs text-primary">
            Invite Confirmation
          </span>
          <div className={`flex items-center gap-3`}>
            <span className="text-xs font-medium text-light-black">{date}</span>
            <span className="text-xs font-medium text-light-black">
              {dayjs(slot?.startTime).format("HH:mm")}
              &nbsp;&nbsp;&nbsp;-&nbsp;&nbsp;&nbsp;
              {dayjs(slot?.endTime).format("HH:mm")}
            </span>
          </div>
        </div>
        {/* Col 2 */}
        <div className="flex flex-col gap-2">
          <span className="font-semibold text-xs text-primary">
            {user?.userType === EnumUserTypes?.RECRUITER
              ? "Status"
              : "Please select your reponse"}
          </span>
          {user?.userType === EnumUserTypes?.RECRUITER ? (
            <div
              className={`flex items-center gap-3 font-medium text-xs text-light-black`}
            >
              {getMeetingStatus(interviewDetails)?.label}
            </div>
          ) : (
            <div
              className={`flex items-center gap-3 ${
                isMeetingDeclined ? "opacity-50 hover" : ""
              }`}
            >
              <button
                onClick={() => handleAcceptOrDecline("accept")}
                disabled={isAccepting || isMeetingDeclined || isAccepted}
                className={`flex gap-2 items-center justify-center font-semibold text-xs text-white bg-primary  rounded-md px-5 py-1.5 ${
                  isMeetingDeclined
                    ? "hover:cursor-not-allowed"
                    : "hover:bg-black"
                }`}
              >
                {isAccepted ? "Accepted" : "Accept"}
                {isAccepting ? <LoadingAnimation className="w-4 h-4" /> : null}
              </button>
              <button
                onClick={() => handleAcceptOrDecline("decline")}
                disabled={isDeclining || isMeetingDeclined}
                className={`flex gap-2 items-center justify-center font-semibold text-xs text-white bg-red-500 ${
                  isMeetingDeclined
                    ? "hover:cursor-not-allowed"
                    : "hover:bg-red-600"
                } rounded-md px-5 py-1.5`}
              >
                Decline{" "}
                {isDeclining ? <LoadingAnimation className="w-4 h-4" /> : null}
              </button>
            </div>
          )}
        </div>
      </div>
      {isMeetingDeclined ? (
        <div className="flex flex-col items-center mt-8 gap-3">
          <span className="text-xs font-medium text-light-black">
            As here the scheduled meeting has been declined, you can propose a
            new time slot.
          </span>
          <button
            onClick={handleCounterProposalPoll}
            className="flex itesm-center justify-center text-white text-xs font-semibold bg-primary rounded-sm py-2 px-4 gap-1"
          >
            <PlusCircleIcon className={"w-4 h-4"} fill={"white"} />
            Propose new times.
          </button>
        </div>
      ) : null}
    </div>
  );
};

export default InviteConfirmationContainer;
